import { useState, useEffect } from 'react';
import { MessageCircle, Users, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { api } from '@/lib/api';

type DialogMode = 'closed' | 'menu' | 'dm' | 'group';

interface NewChatDialogProps {
  onCreateDM: (target: string) => Promise<void>;
  onCreateGroup: (name: string, description: string) => Promise<void>;
  /** Custom trigger element. Omit to use the default small + icon button. */
  trigger?: React.ReactNode;
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false,
  );
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return isMobile;
}

// ---------------------------------------------------------------------------
// Shared panel content
// ---------------------------------------------------------------------------

interface PanelContentProps {
  mode: 'menu' | 'dm' | 'group';
  onSetMode: (m: DialogMode) => void;
  onClose: () => void;
  onSubmitDM: (recipient: string) => Promise<void>;
  onSubmitGroup: (name: string, description: string) => Promise<void>;
}

function PanelContent({ mode, onSetMode, onClose, onSubmitDM, onSubmitGroup }: PanelContentProps) {
  const [recipient, setRecipient] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // DM recipient autocomplete: search active users by username prefix as you type.
  const [results, setResults] = useState<
    Array<{ userId: string; username: string; displayName: string }>
  >([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (mode !== 'dm') return;
    const q = recipient.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await api.searchUsersForDm(q);
        setResults(res.users);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [recipient, mode]);

  // Shared open-DM path: `target` may be a userId (picked from results) or the
  // typed text (an exact username or email), which the server resolves.
  async function selectRecipient(target: string) {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmitDM(target);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open conversation');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitDM() {
    const trimmed = recipient.trim();
    if (!trimmed) return;
    await selectRecipient(trimmed);
  }

  async function handleSubmitGroup() {
    const name = groupName.trim();
    if (!name) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmitGroup(name, groupDescription.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create group');
    } finally {
      setSubmitting(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent, onSubmit: () => void) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
    if (e.key === 'Escape') onClose();
  }

  if (mode === 'menu') {
    return (
      <div className="p-1">
        <button
          onClick={() => onSetMode('dm')}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm rounded-md hover:bg-muted transition-colors"
        >
          <MessageCircle className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <div>
            <div className="font-medium text-foreground">New Message</div>
            <div className="text-xs text-muted-foreground">Start a direct conversation</div>
          </div>
        </button>
        <button
          onClick={() => onSetMode('group')}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm rounded-md hover:bg-muted transition-colors"
        >
          <Users className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
          <div>
            <div className="font-medium text-foreground">New Group</div>
            <div className="text-xs text-muted-foreground">Create a group channel</div>
          </div>
        </button>
      </div>
    );
  }

  if (mode === 'dm') {
    const query = recipient.trim();
    return (
      <div className="p-3">
        <button
          onClick={() => onSetMode('menu')}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors mb-3"
        >
          ← Back
        </button>
        <input
          type="text"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, handleSubmitDM)}
          placeholder="Search by username, or enter an email..."
          className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
          autoFocus
          disabled={submitting}
        />

        {query.length >= 2 && (
          <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-border">
            {searching && results.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">No matching users</div>
            ) : (
              results.map((u) => (
                <button
                  key={u.userId}
                  type="button"
                  onClick={() => selectRecipient(u.userId)}
                  disabled={submitting}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition-colors disabled:opacity-50"
                >
                  <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold uppercase">
                    {(u.displayName || u.username).charAt(0)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {u.displayName || u.username}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      @{u.username}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        )}

        {error && <p className="text-xs text-destructive mt-1.5">{error}</p>}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmitDM}
            disabled={!recipient.trim() || submitting}
            className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Opening...' : 'Open'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <button
        onClick={() => onSetMode('menu')}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors mb-3"
      >
        ← Back
      </button>
      <input
        type="text"
        value={groupName}
        onChange={(e) => setGroupName(e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, handleSubmitGroup)}
        placeholder="Group name..."
        className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 mb-2"
        autoFocus
        disabled={submitting}
      />
      <input
        type="text"
        value={groupDescription}
        onChange={(e) => setGroupDescription(e.target.value)}
        onKeyDown={(e) => handleKeyDown(e, handleSubmitGroup)}
        placeholder="Description (optional)..."
        className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
        disabled={submitting}
      />
      {error && <p className="text-xs text-destructive mt-1.5">{error}</p>}
      <div className="flex justify-end gap-2 mt-3">
        <button
          onClick={onClose}
          className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmitGroup}
          disabled={!groupName.trim() || submitting}
          className="px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {submitting ? 'Creating...' : 'Create'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Default trigger button (used when no custom trigger is provided)
// ---------------------------------------------------------------------------

const DefaultTrigger = (
  <button
    className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
    title="New conversation"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  </button>
);

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function NewChatDialog({ onCreateDM, onCreateGroup, trigger }: NewChatDialogProps) {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<DialogMode>('closed');

  const isOpen = mode !== 'closed';
  const panelMode: 'menu' | 'dm' | 'group' = mode === 'closed' ? 'menu' : mode;
  const panelTitle =
    panelMode === 'menu' ? 'New Conversation' : panelMode === 'dm' ? 'New Message' : 'New Group';

  function handleOpen() {
    setMode('menu');
  }

  function handleClose() {
    setMode('closed');
  }

  // -------------------------------------------------------------------------
  // Mobile — bottom sheet
  // -------------------------------------------------------------------------
  if (isMobile) {
    return (
      <>
        <div
          onClick={isOpen ? undefined : handleOpen}
          className={isOpen ? undefined : 'cursor-pointer'}
        >
          {trigger ?? DefaultTrigger}
        </div>

        {isOpen && (
          <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-40 bg-black/60" onClick={handleClose} />

            {/* Sheet */}
            <div className="fixed bottom-0 inset-x-0 z-50 bg-card border-t border-border rounded-t-2xl animate-in slide-in-from-bottom duration-300 shadow-2xl">
              {/* Drag handle */}
              <div className="pt-3 pb-1 flex justify-center">
                <div className="w-10 h-1 rounded-full bg-border" />
              </div>

              {/* Sheet header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                <span className="text-sm font-semibold">{panelTitle}</span>
                <button
                  onClick={handleClose}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Sheet content */}
              <div className="pb-8">
                <PanelContent
                  mode={panelMode}
                  onSetMode={setMode}
                  onClose={handleClose}
                  onSubmitDM={onCreateDM}
                  onSubmitGroup={onCreateGroup}
                />
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Desktop — Popover (Radix handles avoidCollisions automatically)
  // -------------------------------------------------------------------------
  return (
    <Popover
      open={isOpen}
      onOpenChange={(o) => {
        if (!o) handleClose();
        else if (!isOpen) handleOpen();
      }}
    >
      {trigger ? (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      ) : (
        <PopoverTrigger asChild>{DefaultTrigger}</PopoverTrigger>
      )}

      <PopoverContent
        className="w-64 p-0 bg-card border border-border rounded-lg shadow-xl"
        side="top"
        align="end"
        sideOffset={8}
        avoidCollisions
        collisionPadding={16}
      >
        <PanelContent
          mode={panelMode}
          onSetMode={setMode}
          onClose={handleClose}
          onSubmitDM={onCreateDM}
          onSubmitGroup={onCreateGroup}
        />
      </PopoverContent>
    </Popover>
  );
}
